import { useRef, useEffect, useState } from "react";
import * as d3 from "d3";
import { onlyUnique } from "../../utils/onlyUnique";
import { getClassement } from "./getClassement";

const MARGIN_DESKTOP = { top: 20, right: 150, bottom: 60, left: 50 };
const MARGIN_MOBILE = { top: 20, right: 20, bottom: 100, left: 50 };
const GROUPS = ["Victoire", "Défaite"];
const COLORS = { Victoire: "#377EB8", Défaite: "#eb525c" };

export const Opponents = ({ matchs }) => {
  const containerRef = useRef();
  const svgRef = useRef();
  const [tooltip, setTooltip] = useState(null);

  const rankings = matchs.data
    .map((item) =>
      parseInt(getClassement(item.classement).toString().slice(0, -2), 10),
    )
    .filter(onlyUnique)
    .sort((a, b) => a - b);

  const data = rankings.map((rank) => ({
    ranking: rank,
    Victoire: matchs.data.filter(
      (item) =>
        parseInt(getClassement(item.classement).toString().slice(0, -2), 10) ===
          rank && item.victoire === "V",
    ).length,
    Défaite: matchs.data.filter(
      (item) =>
        parseInt(getClassement(item.classement).toString().slice(0, -2), 10) ===
          rank && item.victoire !== "V",
    ).length,
  }));

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const draw = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      const legendBelow = width < 768;
      const margin = legendBelow ? MARGIN_MOBILE : MARGIN_DESKTOP;
      const innerW = width - margin.left - margin.right;
      const innerH = height - margin.top - margin.bottom;

      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();
      svg.attr("width", width).attr("height", height);

      const g = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      const xScale = d3
        .scaleBand()
        .domain(data.map((d) => d.ranking))
        .range([0, innerW])
        .padding(0.3);

      const xSubScale = d3
        .scaleBand()
        .domain(GROUPS)
        .range([0, xScale.bandwidth()])
        .padding(0.05);

      const yMax = d3.max(data, (d) => Math.max(d.Victoire, d.Défaite)) || 1;

      const yScale = d3
        .scaleLinear()
        .domain([0, yMax + 1])
        .range([innerH, 0]);

      // horizontal grid + Y axis
      g.append("g")
        .call(
          d3
            .axisLeft(yScale)
            .ticks(Math.min(yMax, 6))
            .tickSize(-innerW)
            .tickFormat(d3.format("d"))
            .tickPadding(8),
        )
        .call((ax) => ax.select(".domain").remove())
        .call((ax) =>
          ax
            .selectAll(".tick line")
            .attr("stroke", "#ccc")
            .attr("stroke-width", 1),
        )
        .call((ax) =>
          ax.selectAll(".tick text").attr("font-size", 13).attr("fill", "#555"),
        );

      // X axis
      g.append("g")
        .attr("transform", `translate(0,${innerH})`)
        .call(d3.axisBottom(xScale).tickSize(5).tickPadding(5))
        .call((ax) => ax.select(".domain").attr("stroke", "#ccc"))
        .call((ax) => ax.selectAll(".tick line").attr("stroke", "#ccc"))
        .call((ax) =>
          ax.selectAll(".tick text").attr("font-size", 13).attr("fill", "#555"),
        );

      // X axis label
      g.append("text")
        .attr("x", innerW / 2)
        .attr("y", innerH + 50)
        .attr("text-anchor", "middle")
        .attr("font-size", 14)
        .attr("fill", "#555")
        .text("Classement adversaire");

      // grouped bars
      const groups = g
        .selectAll(".group")
        .data(data)
        .join("g")
        .attr("class", "group")
        .attr("transform", (d) => `translate(${xScale(d.ranking)},0)`);

      GROUPS.forEach((key) => {
        groups
          .append("rect")
          .attr("x", xSubScale(key))
          .attr("y", (d) => yScale(d[key]))
          .attr("width", xSubScale.bandwidth())
          .attr("height", (d) => innerH - yScale(d[key]))
          .attr("fill", COLORS[key])
          .style("cursor", "pointer")
          .on("mouseenter", function (event, d) {
            d3.select(this).attr("opacity", 0.75);
            const rx =
              xScale(d.ranking) +
              xSubScale(key) +
              xSubScale.bandwidth() / 2 +
              margin.left;
            const ry = yScale(d[key]) + margin.top;
            setTooltip({
              xPct: (rx / width) * 100,
              yPct: (ry / height) * 100,
              label: key,
              value: d[key],
              ranking: d.ranking,
            });
          })
          .on("mouseleave", function () {
            d3.select(this).attr("opacity", 1);
            setTooltip(null);
          });

        // value labels inside bars
        groups
          .append("text")
          .filter((d) => d[key] > 0 && innerH - yScale(d[key]) > 18)
          .attr("x", xSubScale(key) + xSubScale.bandwidth() / 2)
          .attr("y", (d) => yScale(d[key]) + 14)
          .attr("text-anchor", "middle")
          .attr("font-size", 12)
          .attr("font-weight", "bold")
          .attr("fill", "white")
          .style("pointer-events", "none")
          .text((d) => d[key]);
      });

      // legend
      if (legendBelow) {
        // horizontal legend centered below the X axis label
        // Each item: 18px rect + 8px gap + ~60px text ≈ 86px; 30px between items
        const itemW = 86;
        const itemGap = 30;
        const totalLegendW =
          GROUPS.length * itemW + (GROUPS.length - 1) * itemGap;
        const legendG = g
          .append("g")
          .attr(
            "transform",
            `translate(${(innerW - totalLegendW) / 2},${innerH + 74})`,
          );
        GROUPS.forEach((key, i) => {
          const row = legendG
            .append("g")
            .attr("transform", `translate(${i * (itemW + itemGap)},0)`);
          row
            .append("rect")
            .attr("width", 18)
            .attr("height", 18)
            .attr("fill", COLORS[key]);
          row
            .append("text")
            .attr("x", 26)
            .attr("y", 9)
            .attr("dominant-baseline", "central")
            .attr("font-size", 14)
            .attr("fill", "#333")
            .text(key);
        });
      } else {
        // legend on the right
        const legendG = svg
          .append("g")
          .attr(
            "transform",
            `translate(${margin.left + innerW + 20},${margin.top + 10})`,
          );
        GROUPS.forEach((key, i) => {
          const row = legendG
            .append("g")
            .attr("transform", `translate(0,${i * 28})`);
          row
            .append("rect")
            .attr("width", 18)
            .attr("height", 18)
            .attr("fill", COLORS[key]);
          row
            .append("text")
            .attr("x", 26)
            .attr("y", 9)
            .attr("dominant-baseline", "central")
            .attr("font-size", 14)
            .attr("fill", "#333")
            .text(key);
        });
      }
    };

    draw();

    const ro = new ResizeObserver(() => {
      setTooltip(null);
      draw();
    });
    ro.observe(container);

    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data)]);

  return (
    <div
      ref={containerRef}
      className="opponents-chart"
      style={{ position: "relative" }}
    >
      <svg ref={svgRef} />
      {tooltip && (
        <div
          className="tooltip-graph"
          style={{
            position: "absolute",
            left: `${tooltip.xPct}%`,
            top: `${tooltip.yPct}%`,
            transform: "translate(-50%, calc(-100% - 8px))",
            pointerEvents: "none",
          }}
        >
          {tooltip.label}
          {tooltip.value > 1 ? "s" : ""} vs.&nbsp;{tooltip.ranking}
          <br />
          <strong>{tooltip.value}</strong>
        </div>
      )}
    </div>
  );
};
